import { useContext, useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import useTicketsData from "./data/ticketsData";
import TicketForm from "./components/TicketForm";
import TicketDetail from "./components/TicketDetail";
import { AuthContext } from "context";

const toTelHref = (phone) => `tel:${String(phone || "").replace(/[^\d+]/g, "")}`;

function Support() {
  const { columns, rows, loadTickets } = useTicketsData();
  const { activePortalSettings } = useContext(AuthContext);
  const [showNewTicketForm, setShowNewTicketForm] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const supportEmail = activePortalSettings?.support_email || "consulting@uds-solutions.de";
  const supportPhone = activePortalSettings?.support_phone || "+49 (0) 4961 7782753";

  const handleNewTicketSubmit = async (ticket) => {
    await loadTickets();
    setShowNewTicketForm(false);
  };

  const handleTicketDetails = (ticketId) => {
    setSelectedTicketId(ticketId);
  };

  const handleTicketClose = async () => {
    setSelectedTicketId(null);
    await loadTickets(); // Aktualisiere die Liste nach Änderungen
  };

  // Angepasste Action-Handler
  const customRows = rows.map(row => {
    const ticketId = row.id.replace('T-', '');
    return {
      ...row,
      actions: (
        <SoftBox display="flex" gap={2}>
          <SoftButton 
            onClick={() => handleTicketDetails(ticketId)} 
            variant="text" 
            color="info" 
            size="sm" 
            iconOnly
          >
            <Icon>chat</Icon>
          </SoftButton>
        </SoftBox>
      ),
      onClick: () => handleTicketDetails(ticketId), // Klick auf Zeile öffnet Details
      style: { cursor: 'pointer' }
    };
  });

  const tableData = {
    columns,
    rows: customRows
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        {/* Support-Optionen */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={6} lg={3}>
            <Card>
              <SoftBox p={3} textAlign="center">
                <SoftBox
                  display="grid"
                  justifyContent="center"
                  alignItems="center"
                  bgColor="info"
                  color="white"
                  width="4rem"
                  height="4rem"
                  shadow="md"
                  borderRadius="lg"
                  mb={2}
                  mx="auto"
                >
                  <Icon fontSize="default">email</Icon>
                </SoftBox>
                <SoftTypography variant="h5" fontWeight="medium" textTransform="capitalize" mb={1}>
                  E-Mail-Support
                </SoftTypography>
                <SoftTypography variant="body2" fontWeight="regular" color="text">
                  {supportEmail}
                </SoftTypography>
                <SoftButton
                  component="a"
                  href={`mailto:${supportEmail}`}
                  variant="gradient"
                  color="info"
                  size="small"
                  sx={{ mt: 3 }}
                >
                  E-Mail senden
                </SoftButton>
              </SoftBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card>
              <SoftBox p={3} textAlign="center">
                <SoftBox
                  display="grid"
                  justifyContent="center"
                  alignItems="center"
                  bgColor="error"
                  color="white"
                  width="4rem"
                  height="4rem"
                  shadow="md"
                  borderRadius="lg"
                  mb={2}
                  mx="auto"
                >
                  <Icon fontSize="default">phone</Icon>
                </SoftBox>
                <SoftTypography variant="h5" fontWeight="medium" textTransform="capitalize" mb={1}>
                  Telefon-Support
                </SoftTypography>
                <SoftTypography variant="body2" fontWeight="regular" color="text">
                  {supportPhone}
                </SoftTypography>
                <SoftButton
                  component="a"
                  href={toTelHref(supportPhone)}
                  variant="gradient"
                  color="error"
                  size="small"
                  sx={{ mt: 3 }}
                >
                  Anrufen
                </SoftButton>
              </SoftBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card>
              <SoftBox p={3} textAlign="center">
                <SoftBox
                  display="grid"
                  justifyContent="center"
                  alignItems="center"
                  bgColor="success"
                  color="white"
                  width="4rem"
                  height="4rem"
                  shadow="md"
                  borderRadius="lg"
                  mb={2}
                  mx="auto"
                >
                  <Icon fontSize="default">chat</Icon>
                </SoftBox>
                <SoftTypography variant="h5" fontWeight="medium" textTransform="capitalize" mb={1}>
                  Live-Chat
                </SoftTypography>
                <SoftTypography variant="body2" fontWeight="regular" color="text">
                  Direkte Hilfe per Chat
                </SoftTypography>
                <SoftButton variant="gradient" color="success" size="small" sx={{ mt: 3 }}>
                  Chat starten
                </SoftButton>
              </SoftBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card>
              <SoftBox p={3} textAlign="center">
                <SoftBox
                  display="grid"
                  justifyContent="center"
                  alignItems="center"
                  bgColor="dark"
                  color="white"
                  width="4rem"
                  height="4rem"
                  shadow="md"
                  borderRadius="lg"
                  mb={2}
                  mx="auto"
                >
                  <Icon fontSize="default">help_center</Icon>
                </SoftBox>
                <SoftTypography variant="h5" fontWeight="medium" textTransform="capitalize" mb={1}>
                  FAQ
                </SoftTypography>
                <SoftTypography variant="body2" fontWeight="regular" color="text">
                  Häufig gestellte Fragen
                </SoftTypography>
                <SoftButton variant="gradient" color="dark" size="small" sx={{ mt: 3 }}>
                  Ansehen
                </SoftButton>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>

        {showNewTicketForm ? (
          <SoftBox mb={3}>
            <TicketForm
              onSubmit={handleNewTicketSubmit}
              onCancel={() => setShowNewTicketForm(false)}
            />
          </SoftBox>
        ) : null}

        {selectedTicketId ? (
          <SoftBox mb={3}>
            <TicketDetail
              ticketId={selectedTicketId}
              onClose={handleTicketClose}
            />
          </SoftBox>
        ) : null}

        {/* Ticket-Übersicht */}
        {!selectedTicketId && !showNewTicketForm && (
          <Card>
            <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" p={3}>
              <SoftBox>
                <SoftTypography variant="h5" fontWeight="medium">
                  Ihre Support-Tickets
                </SoftTypography>
                <SoftTypography variant="button" fontWeight="regular" color="text">
                  Hier finden Sie eine Übersicht Ihrer aktuellen und vergangenen Support-Anfragen
                </SoftTypography>
              </SoftBox>
              <SoftButton
                variant="gradient"
                color="info"
                onClick={() => setShowNewTicketForm(true)}
              >
                Neues Ticket
              </SoftButton>
            </SoftBox>
            <DataTable
              table={tableData}
              entriesPerPage={{
                defaultValue: 10,
                entries: [5, 10, 15, 20],
              }}
              canSearch
            />
          </Card>
        )}
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Support;
